import type { Maybe } from '@adornis/base/utilTypes.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html } from 'lit';
import version from '../../../../../../version.json' with { type: 'json' };
import { getRecordedConsole, getRecordedConsoleCount } from '../../../diagnostics/console-recorder.js';
import { UserAPI } from '../../../users/api/UserAPI.js';
import { SgSnackbar } from '../ui/sg-snackbar.js';

interface ProblemReport {
  description: Maybe<string>;
}

/** Not in lib.dom yet - chromium only, gives the real browser versions instead of the frozen UA string. */
interface NavigatorUserAgentData {
  brands?: { brand: string; version: string }[];
  platform?: string;
}

const collectSystemInfo = (user: { _id: string; username: string; email: string } | undefined, context?: string) => {
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUserAgentData }).userAgentData;
  const brands = userAgentData?.brands;

  return [
    `Zeitpunkt:        ${new Date().toISOString()}`,
    `Seite:            ${location.href}`,
    ...(context ? [`Kontext:          ${context}`] : []),
    `Angemeldet als:   ${user ? `${user.username} <${user.email}> (${user._id})` : 'unbekannt'}`,
    `App-Version:      ${version.release} (${version.commit}), Build ${version.date}`,
    `Browser:          ${brands?.map(b => `${b.brand} ${b.version}`).join(', ') || 'unbekannt'}`,
    `User-Agent:       ${navigator.userAgent}`,
    `Plattform:        ${navigator.platform} / ${userAgentData?.platform ?? 'unbekannt'}`,
    `Hersteller:       ${navigator.vendor || 'unbekannt'}`,
    `Sprache:          ${navigator.language} (${navigator.languages?.join(', ')})`,
    `Zeitzone:         ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    `Fenster:          ${window.innerWidth}×${window.innerHeight} (Pixelverhältnis ${window.devicePixelRatio})`,
    `Bildschirm:       ${screen.width}×${screen.height}`,
    `Online:           ${navigator.onLine ? 'ja' : 'nein'}`,
    `Cookies:          ${navigator.cookieEnabled ? 'aktiviert' : 'deaktiviert'}`,
  ].join('\n');
};

const ReportProblemDialog = stateful((props: { resolve: () => void; reject: () => void; context?: string }) => {
  const user = useObservable(() => currentUser(), []);

  // snapshot on open so what the user sees is exactly what gets sent
  const [systemInfo] = useState(collectSystemInfo(user ?? undefined, props.context), [user]);
  const [consoleLog] = useState(getRecordedConsole());
  const [consoleCount] = useState(getRecordedConsoleCount());

  // useForm memoizes on the document's identity - a fresh literal per render would reset the form
  const [report] = useState({ description: null } satisfies ProblemReport);
  const form = useForm<ProblemReport>(report, { validator: { description: sgNonOptional() } });

  const handleSend = async () => {
    const errors = await form.validate();
    if (errors) return;

    const diagnostics = `${systemInfo}\n\nKonsolenprotokoll:\n${consoleLog}`;

    await guardDialog(async () => UserAPI.reportProblem(form.document.description ?? '', diagnostics)(), {
      success: () => {
        props.resolve();
        SgSnackbar.show('Vielen Dank! Ihre Meldung wurde an die Administration übermittelt.');
      },
    });
  };

  return html`
    <div class="dialog-container">
      ${DialogHeader({ title: 'Problem melden', close: props.reject, closeLabel: 'Dialog schließen' })}

      <p>
        Beschreiben Sie bitte möglichst genau, was nicht funktioniert hat. Zusammen mit Ihrer Beschreibung werden die
        unten aufgeführten technischen Angaben an die Administration geschickt - andere Daten werden nicht übertragen.
      </p>

      <div class="flex flex-col gap-2">
        <h3 class="font-semibold">Was ist passiert?</h3>
        ${TextareaInput({
          ...form.field('description'),
          rows: 6,
          placeholder: 'z. B.: Beim Klick auf "Weiter" in der Sektion Steuerinformationen passiert nichts.',
        })}
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="font-semibold">Systeminformationen</h3>
        <pre class="max-h-64 overflow-auto bg-base-200 rounded p-3 text-sm whitespace-pre-wrap">${systemInfo}</pre>
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="font-semibold">Konsolenprotokoll (${consoleCount} Einträge)</h3>
        <pre class="max-h-64 overflow-auto bg-base-200 rounded p-3 text-sm whitespace-pre-wrap">${consoleLog}</pre>
      </div>

      <button class="btn btn-primary" @click=${handleSend}>Meldung senden</button>
    </div>
  `;
});

/**
 * @param context where the user was when they hit the problem, e.g. the current step of a multi page
 *   form. Shown to the user and sent along with the report.
 */
export async function openReportProblemDialog(context?: string) {
  // showDialog rejects when the user closes the dialog with escape or a click next to it
  return showDialog({
    content: (resolve, reject) => ReportProblemDialog({ resolve, reject, context }),
    modalBoxClasses: 'max-w-3xl',
  }).catch(() => undefined);
}
